import { useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';

const TRIP_DEFAULT = 'juneau-2026';

// Spec §13 — the senior producer (Anar) fires the end-of-trip ceremony
// from the spectator dashboard. Generates the Opus-4.7 narration,
// renders ElevenLabs audio, and persists the slideshow bundle. Re-runs
// with new TARS settings via the same endpoint + force=true.

export default function EndTripPanel() {
  const { tripId } = useTrip();
  const [humor, setHumor] = useState(75);
  const [honesty, setHonesty] = useState(95);
  const [sarcasm, setSarcasm] = useState(60);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [mp4, setMp4] = useState(null);
  const [mp4Busy, setMp4Busy] = useState(false);

  const easter = humor === 100 && honesty === 100;

  const renderMp4 = async () => {
    setMp4Busy(true);
    setError(null);
    try {
      const res = await fetch('/api/slideshow/render-mp4', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ trip_id: tripId || TRIP_DEFAULT }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (!res.ok) {
        setError(data.message || data.error || `mp4 render → HTTP ${res.status}`);
        return;
      }
      setMp4(data);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setMp4Busy(false);
    }
  };

  // Trip-end is a background function — kick it off, then poll
  // /api/slideshow/data until mode === 'final'.
  const pollFinal = async () => {
    const t0 = Date.now();
    const TIMEOUT = 6 * 60 * 1000;
    while (Date.now() - t0 < TIMEOUT) {
      const r = await fetch(
        `/api/slideshow/data?trip_id=${encodeURIComponent(tripId || TRIP_DEFAULT)}`
      );
      const d = await r.json().catch(() => ({}));
      if (d.mode === 'final' && d.audio_url) return d;
      await new Promise((res) => setTimeout(res, 8000));
    }
    throw new Error('render timed out (>6 min)');
  };

  const fire = async (force) => {
    if (
      !force &&
      !window.confirm(
        'Generate the trip-end slideshow narration now? This calls Claude Opus + ElevenLabs (~$0.40 per render). Render runs in the background; this dialog will update when it finishes (1–3 minutes).'
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/trip-end', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          trip_id: tripId || TRIP_DEFAULT,
          humor,
          honesty,
          sarcasm,
          force: !!force,
        }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (!res.ok && res.status !== 202) {
        setError(data.message || data.error || `HTTP ${res.status}`);
        return;
      }
      // 202 = background render kicked off; cached bundle returns 200.
      if (res.status === 202 || data.state === 'rendering') {
        const final = await pollFinal();
        setResult(final);
      } else {
        setResult(data);
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card overflow-hidden border-copper/40">
      <header className="border-b border-copper/40 bg-copper/10 px-4 py-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
          end of trip · senior producer
        </p>
        <p className="mt-1 text-sm text-fg-primary">
          Run the closing narration. The slideshow page (
          <a
            href={`/slideshow/${tripId || TRIP_DEFAULT}`}
            target="_blank"
            rel="noreferrer noopener"
            className="text-action underline-offset-4 hover:underline"
          >
            /slideshow/{tripId || TRIP_DEFAULT}
          </a>
          ) refreshes live; tap &ldquo;final cut&rdquo; once narration is generated.
        </p>
      </header>
      <div className="space-y-3 px-4 py-3">
        <Slider label="Humor" value={humor} onChange={setHumor} />
        <Slider label="Honesty" value={honesty} onChange={setHonesty} />
        <Slider label="Sarcasm" value={sarcasm} onChange={setSarcasm} />
        {easter && (
          <p className="rounded-md border border-copper/40 bg-copper/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            tars dial · maximum reflection (Wilman will acknowledge it)
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fire(false)}
            disabled={busy}
            className="rounded-md border border-action bg-action px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-action-fg disabled:opacity-50"
          >
            {busy ? 'rendering…' : result ? 'generate again' : 'generate slideshow narration'}
          </button>
          {result && (
            <button
              type="button"
              onClick={() => fire(true)}
              disabled={busy}
              className="rounded-md border border-copper/60 bg-copper/10 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-copper-dark disabled:opacity-50"
            >
              re-render with these settings
            </button>
          )}
        </div>
        {error && (
          <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
            {error}
          </p>
        )}
        {result && (
          <div className="rounded-md border border-success/40 bg-success/10 p-3 text-xs text-fg-primary">
            <p className="font-mono text-[10px] uppercase tracking-wider text-success">
              {result.cached ? 'cached bundle returned' : 'fresh render'}
              {result.audio_duration ? ` · audio ${result.audio_duration.toFixed(1)}s` : ''}
            </p>
            {result.audio_url && (
              <audio src={result.audio_url} controls className="mt-2 w-full" />
            )}
            {result.script && (
              <details className="mt-2">
                <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  view script
                </summary>
                <p className="mt-1 whitespace-pre-wrap font-serif italic text-fg-secondary">
                  {result.script}
                </p>
              </details>
            )}
            <div className="mt-3 border-t border-success/30 pt-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={renderMp4}
                  disabled={mp4Busy}
                  className="rounded-md border border-action bg-action px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-action-fg disabled:opacity-50"
                >
                  {mp4Busy ? 'requesting MP4 render…' : mp4 ? 're-render MP4' : 'render Cloudinary MP4'}
                </button>
                <a
                  href={`/api/happywhale/export?trip_id=${tripId || TRIP_DEFAULT}&format=csv`}
                  className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary hover:bg-bg-tertiary"
                >
                  download HappyWhale CSV
                </a>
                <a
                  href={`/api/happywhale/export?trip_id=${tripId || TRIP_DEFAULT}&format=json`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary hover:bg-bg-tertiary"
                >
                  view HappyWhale JSON
                </a>
              </div>
              {mp4 && (
                <div className="mt-2 space-y-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  <p>status · {mp4.status} · {mp4.slide_count} slides · ~{Math.round(mp4.duration)}s</p>
                  <p className="normal-case tracking-normal text-fg-secondary">
                    public_id · <code>{mp4.public_id}</code>
                  </p>
                  {mp4.secure_url && (
                    <p className="normal-case tracking-normal">
                      <a
                        href={mp4.secure_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-action underline-offset-4 hover:underline"
                      >
                        open MP4 ↗
                      </a>
                    </p>
                  )}
                  <p className="text-fg-tertiary">
                    Cloudinary renders asynchronously (~1–5 min). The MP4 URL becomes valid once
                    the render completes; refresh this panel to check.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Slider({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        <span>{label}</span>
        <span className="text-fg-secondary">{value}</span>
      </span>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="mt-1 w-full"
      />
    </label>
  );
}
